import { createInitialDraft, INITIAL_STEP_STATUSES } from '../utils/constants';

function normalizeUserRow(raw, index) {
  const legacyName = String(raw.name ?? '');
  const parts = legacyName.trim().split(/\s+/);
  return {
    id: String(raw.id ?? `user-${index}`),
    first_name: String(raw.first_name ?? parts[0] ?? ''),
    last_name: String(raw.last_name ?? parts.slice(1).join(' ') ?? ''),
    username: String(raw.username ?? raw.email ?? ''),
    email: String(raw.email ?? ''),
    mobile: String(raw.mobile ?? raw.phone ?? ''),
    is_superuser: Boolean(raw.is_superuser),
    status: raw.status === 'Active' ? 'Active' : 'Pending',
    name: legacyName || undefined,
    role: raw.role != null ? String(raw.role) : undefined,
  };
}

function asStepStatus(value) {
  if (value === 'completed' || value === 'skipped' || value === 'in_progress') {
    return value;
  }
  return 'pending';
}

function digitsOnly(value) {
  return String(value ?? '').replace(/\D/g, '');
}

function normalizeWhatsappDraft(raw) {
  const base = createInitialDraft().whatsapp;
  if (!raw || typeof raw !== 'object') return base;
  return {
    app_id: String(raw.app_id ?? raw.appId ?? '').trim(),
    app_name: String(raw.app_name ?? raw.appName ?? '').trim(),
    app_type: String(raw.app_type ?? raw.appType ?? 'whatsapp').trim() || 'whatsapp',
    wa_phone_number: digitsOnly(raw.wa_phone_number ?? raw.whatsappNumber),
    wa_phone_number_id: digitsOnly(raw.wa_phone_number_id ?? raw.phoneNumberId),
    waba_id: String(raw.waba_id ?? raw.wabaId ?? '').trim(),
    user_access_token: String(raw.user_access_token ?? raw.userAccessToken ?? '').trim(),
    is_active:
      raw.is_active !== undefined
        ? Boolean(raw.is_active)
        : raw.isActive !== undefined
          ? Boolean(raw.isActive)
          : true,
    mongo_id: raw.mongo_id != null ? String(raw.mongo_id) : null,
  };
}

function normalizeFormFieldRow(raw, index) {
  return {
    id: String(raw.id ?? `field-${index}`),
    alias_name: String(raw.alias_name ?? raw.label ?? ''),
    key_name: String(raw.key_name ?? raw.key ?? ''),
    key_type: String(raw.key_type ?? raw.type ?? 'text'),
    is_required: Boolean(raw.is_required ?? raw.required ?? raw.isRequired ?? false),
    label: raw.label != null ? String(raw.label) : undefined,
    type: raw.type != null ? String(raw.type) : undefined,
    required: raw.required != null ? Boolean(raw.required) : undefined,
  };
}

function normalizeAccountContact(raw, index) {
  return {
    id: String(raw.id ?? `contact-${index}`),
    name: String(raw.name ?? ''),
    email: String(raw.email ?? ''),
    phone: String(raw.phone ?? raw.mobile ?? ''),
    designation: String(raw.designation ?? ''),
    department: String(raw.department ?? ''),
    singlePointOfContact: String(
      raw.singlePointOfContact ?? (raw.spoc === true ? 'yes' : raw.spoc === false ? 'no' : '')
    ),
  };
}

function normalizeWorkflow(raw) {
  if (!raw || typeof raw !== 'object') return {};
  return {
    clone_from_flow_id: raw.clone_from_flow_id != null ? String(raw.clone_from_flow_id) : null,
    clone_from_title: raw.clone_from_title != null ? String(raw.clone_from_title) : null,
    flow_id: raw.flow_id != null ? String(raw.flow_id) : null,
    title: raw.title != null ? String(raw.title) : null,
  };
}

function normalizeCenters(raw) {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const center = entry.center;
      if (!center || typeof center !== 'object') return null;
      return {
        center: {
          center_name: String(center.center_name ?? ''),
          address: String(center.address ?? ''),
          latitude: String(center.latitude ?? ''),
          longitude: String(center.longitude ?? ''),
          radius: Number(center.radius ?? 0),
          subarea: String(center.subarea ?? ''),
          area: String(center.area ?? ''),
          city: String(center.city ?? ''),
          pincode: String(center.pincode ?? ''),
          codes: Array.isArray(center.codes) ? center.codes.map((code) => String(code)) : [],
        },
      };
    })
    .filter(Boolean);
}

function normalizeCampaignRow(raw) {
  const fields = Array.isArray(raw.form_fields)
    ? raw.form_fields.map(normalizeFormFieldRow)
    : [];
  return {
    goal: String(raw.goal ?? ''),
    name: String(raw.name ?? ''),
    template_id: raw.template_id != null ? Number(raw.template_id) : null,
    form_fields: fields,
    proposal_id: raw.proposal_id != null ? String(raw.proposal_id) : null,
    entity_form_id: raw.entity_form_id != null ? String(raw.entity_form_id) : null,
    workflow: normalizeWorkflow(raw.workflow),
    tentative_cost: raw.tentative_cost != null ? Number(raw.tentative_cost) : null,
    cost_of_lead: raw.cost_of_lead != null ? Number(raw.cost_of_lead) : null,
    brand: raw.brand != null ? String(raw.brand) : null,
    description: raw.description != null ? String(raw.description) : '',
    principal_vendor: raw.principal_vendor != null ? String(raw.principal_vendor) : undefined,
    account_id: raw.account_id != null ? String(raw.account_id) : undefined,
    organisation_id: raw.organisation_id != null ? String(raw.organisation_id) : undefined,
    vendorData: Array.isArray(raw.vendorData) ? raw.vendorData.map((value) => Number(value)) : undefined,
    centers: normalizeCenters(raw.centers),
  };
}

function migrateLegacyCampaignDraft(apiDraft, base) {
  const legacy = apiDraft.campaign;
  const legacyFields = Array.isArray(apiDraft.formFields)
    ? apiDraft.formFields.map(normalizeFormFieldRow)
    : [];
  const goal = String(legacy?.goal ?? '').trim();
  const name = String(legacy?.name ?? '').trim();
  if (!goal && !name && legacyFields.length === 0) {
    return base.campaign_setup;
  }
  return {
    primary_goal: goal || 'lead_generation',
    campaigns: [
      {
        goal: goal || 'lead_generation',
        name,
        template_id: legacy?.template_id != null ? Number(legacy.template_id) : null,
        form_fields: legacyFields,
        proposal_id: legacy?.proposal_id != null ? String(legacy.proposal_id) : null,
        entity_form_id: legacy?.entity_form_id != null ? String(legacy.entity_form_id) : null,
        workflow: {},
        tentative_cost: legacy?.tentative_cost != null ? Number(legacy.tentative_cost) : null,
        cost_of_lead: legacy?.cost_of_lead != null ? Number(legacy.cost_of_lead) : null,
        brand: legacy?.brand != null ? String(legacy.brand) : null,
        description: legacy?.description != null ? String(legacy.description) : '',
        principal_vendor: legacy?.principal_vendor != null ? String(legacy.principal_vendor) : undefined,
        account_id: legacy?.account_id != null ? String(legacy.account_id) : undefined,
        organisation_id: legacy?.organisation_id != null ? String(legacy.organisation_id) : undefined,
        vendorData: Array.isArray(legacy?.vendorData)
          ? legacy.vendorData.map((value) => Number(value))
          : undefined,
        centers: normalizeCenters(legacy?.centers),
      },
    ],
  };
}

function normalizeCampaignSetup(apiDraft, base) {
  const raw = apiDraft.campaign_setup;
  if (!raw || typeof raw !== 'object') {
    return migrateLegacyCampaignDraft(apiDraft, base);
  }
  const campaigns = Array.isArray(raw.campaigns) ? raw.campaigns.map(normalizeCampaignRow) : [];
  return {
    primary_goal: String(raw.primary_goal ?? campaigns[0]?.goal ?? ''),
    campaigns,
  };
}

export function normalizeDraft(apiDraft) {
  const base = createInitialDraft();
  if (!apiDraft || typeof apiDraft !== 'object') return base;

  const org = apiDraft.organisation || {};
  const profileUsers = apiDraft.profile_users || {
    clone_from_profile_id: null,
    profile_name: '',
  };
  const account = apiDraft.account || {};
  const mapping = apiDraft.organisation_mapping || base.organisation_mapping;

  return {
    ...base,
    organisation: {
      ...base.organisation,
      ...org,
      license_id: org.license_id != null ? String(org.license_id) : base.organisation.license_id,
      license_alias: org.license_alias != null ? String(org.license_alias) : base.organisation.license_alias,
      license_permission_profile_id:
        org.license_permission_profile_id != null
          ? String(org.license_permission_profile_id)
          : base.organisation.license_permission_profile_id,
      license_permission_profile_name:
        org.license_permission_profile_name != null
          ? String(org.license_permission_profile_name)
          : base.organisation.license_permission_profile_name,
    },
    account: {
      ...base.account,
      account_id: account.account_id != null ? String(account.account_id) : base.account.account_id,
      name: String(account.name ?? ''),
      phone: String(account.phone ?? ''),
      email: String(account.email ?? ''),
      address: String(account.address ?? ''),
      reference_name: String(account.reference_name ?? ''),
      reference_phone: String(account.reference_phone ?? ''),
      reference_email: String(account.reference_email ?? ''),
      comments: String(account.comments ?? ''),
      organisation_id:
        account.organisation_id != null ? String(account.organisation_id) : base.account.organisation_id,
      contacts: Array.isArray(account.contacts)
        ? account.contacts.map(normalizeAccountContact)
        : base.account.contacts,
    },
    organisation_mapping: {
      firstCategory: mapping.firstCategory ?? null,
      firstOrganisation: mapping.firstOrganisation ?? null,
      secondCategory: mapping.secondCategory ?? null,
      secondOrganisation: mapping.secondOrganisation ?? null,
    },
    profile_users: {
      clone_from_profile_id: profileUsers.clone_from_profile_id ?? null,
      profile_name: profileUsers.profile_name ?? profileUsers.new_name ?? '',
      permissions_source: profileUsers.permissions_source ?? 'license',
    },
    objectPermissions: apiDraft.objectPermissions || base.objectPermissions,
    generalPermissions: apiDraft.generalPermissions || base.generalPermissions,
    users: Array.isArray(apiDraft.users) ? apiDraft.users.map(normalizeUserRow) : base.users,
    whatsapp: normalizeWhatsappDraft(apiDraft.whatsapp),
    campaign_setup: normalizeCampaignSetup(apiDraft, base),
  };
}

export function sessionToClientState(session) {
  const steps = { ...INITIAL_STEP_STATUSES };
  Object.entries(session.step_status || {}).forEach(([key, value]) => {
    if (key in steps) {
      steps[key] = asStepStatus(String(value));
    }
  });

  return {
    sessionId: session.session_id,
    organisationId: session.organisation_id ?? null,
    profileId: session.profile_id ?? null,
    currentStep: session.current_step || 1,
    steps,
    draft: normalizeDraft(session.draft),
  };
}

export function draftSliceForStep(stepKey, draft) {
  switch (stepKey) {
    case 'organisation':
      return { ...draft.organisation };
    case 'account':
      return { ...draft.account };
    case 'organisation_mapping':
      return { ...draft.organisation_mapping };
    case 'profile_users':
      return {
        profile_users: draft.profile_users ?? {
          clone_from_profile_id: null,
          profile_name: '',
        },
        objectPermissions: draft.objectPermissions,
        generalPermissions: draft.generalPermissions,
        users: draft.users,
      };
    case 'whatsapp':
      return { ...draft.whatsapp };
    case 'campaign':
      return { ...draft.campaign_setup };
    case 'form':
      return {
        campaigns: draft.campaign_setup.campaigns.map((c) => ({
          goal: c.goal,
          form_fields: c.form_fields,
        })),
      };
    default:
      return {};
  }
}

export function mapStepErrors(errors) {
  if (!errors || typeof errors !== 'object') return {};
  const obj = errors;
  if (obj.general_error != null && typeof obj.general_error === 'object') {
    return mapStepErrors(obj.general_error);
  }
  if (typeof obj.general_error === 'string') {
    return { general: obj.general_error };
  }
  const flat = {};
  const walk = (walkObj, prefix = '') => {
    Object.entries(walkObj).forEach(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (typeof value === 'string') {
        flat[path] = value;
        if (key === 'phone') flat.mobile = value;
      } else if (Array.isArray(value) && value[0]) {
        flat[path] = String(value[0]);
      } else if (value && typeof value === 'object') {
        walk(value, path);
      }
    });
  };
  walk(errors);
  return flat;
}
